'use client';

import { useState } from 'react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import ScoreGauge from '@/components/tools/ScoreGauge';
import ProgressBar from '@/components/tools/ProgressBar';
import { Flame } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Severity = 'critical' | 'major' | 'minor';

interface RoastProblem {
  severity: Severity;
  headline: string;
  explanation: string;
  fix: string;
  examples: string[];
  /** Internal score penalty (higher = worse), used for ranking */
  penalty: number;
}

interface RoastPositive {
  text: string;
}

interface RoastResult {
  score: number;
  verdict: string;
  problems: RoastProblem[];
  positives: RoastPositive[];
  breakdown: { label: string; value: number }[];
}

/* ------------------------------------------------------------------ */
/*  Word / pattern lists                                               */
/* ------------------------------------------------------------------ */

const WEAK_PHRASES = [
  'responsible for', 'duties included', 'helped with', 'assisted in',
  'worked on', 'involved in', 'participated in', 'tasked with',
  'in charge of', 'handled', 'dealt with', 'was responsible',
  'helped to', 'assisted with', 'was involved',
];

const BUZZWORDS = [
  'synergy', 'synergies', 'paradigm', 'holistic', 'bandwidth',
  'circle back', 'move the needle', 'thought leader', 'best-in-class',
  'game-changer', 'rockstar', 'ninja', 'guru', 'wizard', 'unicorn',
  'wear many hats', 'go-getter', 'self-starter', 'team player',
  'detail-oriented', 'hard worker', 'fast learner', 'people person',
  'results-oriented', 'dynamic', 'proactive', 'passionate',
  'leverage', 'ecosystem', 'disruptive', 'bleeding edge',
  'value-add', 'core competency', 'deep dive', 'low-hanging fruit',
  'think outside the box', 'hit the ground running',
];

const CLICHE_OBJECTIVES = [
  'seeking a challenging position',
  'looking for an opportunity',
  'to obtain a position',
  'seeking employment',
  'to utilize my skills',
  'looking to leverage',
  'seeking a role where',
  'a position that allows me',
  'to gain experience',
  'eager to contribute',
  'passionate about',
  'dedicated professional seeking',
];

const GENERIC_STATEMENTS = [
  'excellent communication skills',
  'strong work ethic',
  'works well under pressure',
  'great attention to detail',
  'proven track record',
  'strong interpersonal skills',
  'motivated individual',
  'highly motivated',
  'strong analytical skills',
  'excellent organizational skills',
  'ability to multitask',
  'able to work independently',
  'good time management',
  'quick learner',
];

const EXPERIENCE_HEADERS = [
  'experience', 'work experience', 'professional experience', 'employment',
  'employment history', 'work history',
];

const EDUCATION_HEADERS = [
  'education', 'academic background', 'academic',
];

const SKILLS_HEADERS = [
  'skills', 'technical skills', 'core competencies', 'competencies',
  'technologies', 'tools', 'proficiencies',
];

const SUMMARY_HEADERS = [
  'summary', 'professional summary', 'profile', 'about',
];

const STRONG_ACTION_VERBS = [
  'achieved', 'accelerated', 'accomplished', 'administered', 'advanced',
  'analyzed', 'architected', 'automated', 'boosted', 'built', 'captured',
  'championed', 'consolidated', 'created', 'cultivated', 'decreased',
  'delivered', 'designed', 'developed', 'directed', 'drove', 'earned',
  'eliminated', 'enabled', 'engineered', 'established', 'exceeded',
  'executed', 'expanded', 'expedited', 'facilitated', 'formulated',
  'founded', 'generated', 'grew', 'headed', 'identified', 'implemented',
  'improved', 'increased', 'influenced', 'initiated', 'innovated',
  'introduced', 'launched', 'led', 'leveraged', 'managed', 'maximized',
  'mentored', 'modernized', 'negotiated', 'optimized', 'orchestrated',
  'outperformed', 'overhauled', 'partnered', 'piloted', 'pioneered',
  'produced', 'programmed', 'propelled', 'ranked', 'rebuilt', 'reduced',
  'reengineered', 'reorganized', 'resolved', 'restructured', 'revamped',
  'scaled', 'secured', 'simplified', 'spearheaded', 'streamlined',
  'strengthened', 'surpassed', 'transformed', 'tripled', 'unified',
  'upgraded',
];

/* ------------------------------------------------------------------ */
/*  Helper: find matching lines from the resume                        */
/* ------------------------------------------------------------------ */

function findMatchingLines(text: string, patterns: string[], max: number = 3): string[] {
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const matches: string[] = [];
  const lowerText = text.toLowerCase();

  for (const pattern of patterns) {
    if (matches.length >= max) break;
    if (lowerText.includes(pattern.toLowerCase())) {
      // Find the actual line containing this pattern
      const matchLine = lines.find(
        (l) => l.toLowerCase().includes(pattern.toLowerCase()),
      );
      if (matchLine) {
        const trimmed = matchLine.trim();
        // Only include if not already added (avoid duplicates)
        if (!matches.includes(trimmed) && trimmed.length < 200) {
          matches.push(trimmed);
        }
      }
    }
  }

  return matches;
}

/** Check if a section header exists in the text */
function hasSection(text: string, headers: string[]): boolean {
  const lowerText = text.toLowerCase();
  return headers.some((h) => {
    const regex = new RegExp(`(^|\\n)\\s*${h}\\s*($|\\n|:|-|\\|)`, 'i');
    return regex.test(lowerText);
  });
}

/* ------------------------------------------------------------------ */
/*  Individual roast checks                                            */
/* ------------------------------------------------------------------ */

function checkWeakLanguage(text: string): RoastProblem | null {
  const lowerText = text.toLowerCase();
  const found = WEAK_PHRASES.filter((p) => lowerText.includes(p));
  if (found.length === 0) return null;

  const examples = findMatchingLines(text, found);
  const severity: Severity = found.length >= 4 ? 'critical' : found.length >= 2 ? 'major' : 'minor';
  const penalty = found.length * 6;

  return {
    severity,
    headline: "You Sound Like You're Reading a Job Description, Not Selling Yourself",
    explanation: `Your resume contains ${found.length} passive, wishy-washy phrase${found.length > 1 ? 's' : ''} like "${found[0]}." Recruiters read hundreds of resumes. Phrases like "responsible for" and "helped with" scream "I did the bare minimum." They want someone who OWNED results, not someone who was vaguely nearby when things happened.`,
    fix: `Replace every "responsible for" with a power verb. Instead of "Responsible for managing a team," write "Led a team of 8 engineers to deliver a $2M platform migration ahead of schedule." Own your work.`,
    examples,
    penalty,
  };
}

function checkMissingMetrics(text: string): RoastProblem | null {
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const linesWithNumbers = lines.filter((l) =>
    /\d+[%$kKmMbB]|\$\d|#\d|\d+\s*%|\d+x|\d+\+|\d{1,3},\d{3}/.test(l),
  );
  const ratio = lines.length > 0 ? linesWithNumbers.length / lines.length : 0;

  if (ratio > 0.25) return null;

  const severity: Severity = ratio < 0.05 ? 'critical' : ratio < 0.15 ? 'major' : 'minor';
  const penalty = severity === 'critical' ? 28 : severity === 'major' ? 18 : 10;

  // Find lines that SHOULD have numbers but don't (look for achievement-like lines)
  const badExamples = lines
    .filter((l) => {
      const lower = l.toLowerCase();
      return (
        !(/\d/.test(l)) &&
        (lower.includes('improved') || lower.includes('increased') ||
         lower.includes('reduced') || lower.includes('managed') ||
         lower.includes('led') || lower.includes('grew') ||
         lower.includes('saved') || lower.includes('delivered') ||
         lower.includes('created') || lower.includes('built'))
      );
    })
    .slice(0, 3)
    .map((l) => l.trim());

  return {
    severity,
    headline: 'Where Are the Numbers?!',
    explanation: `Only ${linesWithNumbers.length} out of ${lines.length} lines contain any metrics. Your resume is all "what" and no "how much." Without numbers, your achievements are just... opinions. "Improved performance" means nothing. Improved it by 2%? 200%? A recruiter will assume the worst.`,
    fix: `Add at least one number to every bullet point. Revenue, percentages, team sizes, time saved, users served -- anything. "Managed social media" becomes "Grew Instagram following from 5K to 50K in 6 months, driving a 300% increase in engagement."`,
    examples: badExamples.length > 0 ? badExamples : ['(Multiple lines lack any quantified results)'],
    penalty,
  };
}

function checkLength(text: string): RoastProblem | null {
  const wordCount = text.split(/\s+/).filter((w) => w.length > 0).length;

  if (wordCount >= 200 && wordCount <= 800) return null;

  const tooShort = wordCount < 200;
  const severity: Severity = (wordCount < 100 || wordCount > 1200) ? 'critical' : 'major';
  const penalty = severity === 'critical' ? 22 : 14;

  if (tooShort) {
    return {
      severity,
      headline: "Is This a Resume or a Post-It Note?",
      explanation: `Your resume is only ~${wordCount} words. That is suspiciously short. Either you copy-pasted it wrong, or you are dramatically underselling yourself. A recruiter spending 6 seconds on this will think "not enough experience" and move on. Even a recent grad should have more to say.`,
      fix: `Aim for 300-700 words (roughly one full page). Expand your bullet points with specific achievements, add a skills section, and include a professional summary. If you have volunteer work, projects, or coursework -- put them in.`,
      examples: [`(Resume is approximately ${wordCount} words -- aim for 300-700)`],
      penalty,
    };
  }

  return {
    severity,
    headline: 'This Resume Has More Pages Than a Novel',
    explanation: `Your resume clocks in at ~${wordCount} words. Unless you are a tenured professor or a C-suite executive with 30 years of experience, nobody needs to read all this. Recruiters spend 6-7 seconds scanning a resume. If they have to scroll through War and Peace, they will not.`,
    fix: `Trim to 400-700 words. Cut anything older than 10-15 years. Remove obvious skills ("Microsoft Word" -- really?). Consolidate roles. If a bullet point does not prove you can do THIS job, it goes.`,
    examples: [`(Resume is approximately ${wordCount} words -- trim to under 800)`],
    penalty,
  };
}

function checkMissingSections(text: string): RoastProblem | null {
  const missing: string[] = [];

  if (!hasSection(text, EXPERIENCE_HEADERS)) missing.push('Experience');
  if (!hasSection(text, EDUCATION_HEADERS)) missing.push('Education');
  if (!hasSection(text, SKILLS_HEADERS)) missing.push('Skills');
  if (!hasSection(text, SUMMARY_HEADERS) && !hasSection(text, ['objective', 'career objective'])) {
    missing.push('Summary/Objective');
  }

  if (missing.length === 0) return null;

  const severity: Severity = missing.length >= 3 ? 'critical' : missing.length >= 2 ? 'major' : 'minor';
  const penalty = missing.length * 7;

  return {
    severity,
    headline: missing.length >= 3
      ? "Did You Forget To Write Most of Your Resume?"
      : `Your Resume is Missing: ${missing.join(', ')}`,
    explanation: `Your resume is missing ${missing.length} key section${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}. This is like showing up to a job interview without pants. These sections are not optional -- they are the bare minimum that every recruiter and ATS system expects. Without them, your resume might get auto-rejected before a human even sees it.`,
    fix: `Add clearly labeled sections for ${missing.join(', ')}. Use standard header names (not creative alternatives). ATS systems parse resumes by looking for these exact section names.`,
    examples: [`Missing sections: ${missing.join(', ')}`],
    penalty,
  };
}

function checkBuzzwords(text: string): RoastProblem | null {
  const lowerText = text.toLowerCase();
  const found = BUZZWORDS.filter((bw) => {
    const regex = new RegExp(`\\b${bw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(lowerText);
  });

  if (found.length <= 1) return null;

  const examples = findMatchingLines(text, found);
  const severity: Severity = found.length >= 6 ? 'critical' : found.length >= 3 ? 'major' : 'minor';
  const penalty = found.length * 4;

  return {
    severity,
    headline: 'Congratulations, You Won Buzzword Bingo',
    explanation: `Your resume contains ${found.length} overused buzzwords: "${found.slice(0, 5).join('", "')}"${found.length > 5 ? ` and ${found.length - 5} more` : ''}. Every single applicant calls themselves a "team player" who is "detail-oriented" and "results-oriented." These words have been used so many times they have lost all meaning. Recruiters mentally skip over them.`,
    fix: `Kill every buzzword and replace it with a specific example. Don't say "team player" -- say "Collaborated with design and engineering teams to ship 3 product features in Q1." Show, don't tell.`,
    examples,
    penalty,
  };
}

function checkFormattingSignals(text: string): RoastProblem | null {
  const lines = text.split('\n');
  const nonEmptyLines = lines.filter((l) => l.trim().length > 0);

  // Check for bullet points
  const bulletLines = nonEmptyLines.filter((l) =>
    /^\s*[-*\u2022\u25CF\u25CB\u2023\u2043\u25AA\u25AB\u2013\u2014\u27A4>]/.test(l.trim()) ||
    /^\s*\d+[.)]\s/.test(l.trim()),
  );
  const bulletRatio = nonEmptyLines.length > 0 ? bulletLines.length / nonEmptyLines.length : 0;

  // Check for wall-of-text (long lines with no breaks)
  const longLines = nonEmptyLines.filter((l) => l.trim().length > 150);
  const hasWallOfText = longLines.length >= 3;

  // Check for very short paragraphs / inconsistent formatting
  const avgLineLength = nonEmptyLines.reduce((sum, l) => sum + l.trim().length, 0) / (nonEmptyLines.length || 1);

  if (bulletRatio > 0.25 && !hasWallOfText) return null;

  const severity: Severity = (bulletRatio < 0.1 && hasWallOfText) ? 'critical' : bulletRatio < 0.15 ? 'major' : 'minor';
  const penalty = severity === 'critical' ? 20 : severity === 'major' ? 14 : 8;

  const issues: string[] = [];
  if (bulletRatio < 0.15) issues.push('almost no bullet points');
  if (hasWallOfText) issues.push('walls of text');
  if (avgLineLength > 120) issues.push('overly long paragraphs');

  return {
    severity,
    headline: hasWallOfText ? 'My Eyes Are Bleeding From This Wall of Text' : 'Where Are the Bullet Points?',
    explanation: `Your resume has ${issues.join(' and ')}. Recruiters scan resumes in 6 seconds. If they see a dense block of text, they are not going to read it -- they are going to skip to the next candidate. Your resume needs to be scannable at a glance.`,
    fix: `Break every role into 3-5 concise bullet points. Each bullet should be 1-2 lines max. Start each with an action verb. Use white space generously. If a recruiter cannot find your key achievements in 6 seconds, you have already lost.`,
    examples: longLines.slice(0, 2).map((l) =>
      l.trim().length > 100 ? l.trim().substring(0, 100) + '...' : l.trim(),
    ),
    penalty,
  };
}

function checkPronouns(text: string): RoastProblem | null {
  const pronounPattern = /\b(I|me|my|myself|mine)\b/g;
  const matches: string[] = text.match(pronounPattern) ?? [];

  // Filter out false positives (e.g. "AI" matching "I")
  const realMatches = matches.filter((m: string) => {
    // "I" at the start of a sentence or after certain words is likely a real pronoun
    return m.length > 1 || m === 'I';
  });

  if (realMatches.length <= 2) return null;

  const examples = findMatchingLines(text, ['I ', ' me ', ' my ', ' myself']);
  const severity: Severity = realMatches.length >= 8 ? 'major' : 'minor';
  const penalty = realMatches.length * 2;

  return {
    severity,
    headline: "It's Not a Diary -- Drop the 'I'",
    explanation: `Your resume uses first-person pronouns ${realMatches.length} times. Professional resumes are written in implied first person -- no "I," "me," or "my." Using first person makes your resume read like a cover letter (or worse, a LinkedIn post). It also wastes precious space on a word that adds zero information.`,
    fix: `Remove every "I," "me," and "my." Instead of "I managed a team of 10," write "Managed a team of 10." The subject is always you -- the reader knows that.`,
    examples,
    penalty,
  };
}

function checkGenericStatements(text: string): RoastProblem | null {
  const lowerText = text.toLowerCase();
  const found = GENERIC_STATEMENTS.filter((stmt) => lowerText.includes(stmt));

  if (found.length <= 1) return null;

  const examples = findMatchingLines(text, found);
  const severity: Severity = found.length >= 5 ? 'major' : found.length >= 3 ? 'major' : 'minor';
  const penalty = found.length * 4;

  return {
    severity,
    headline: 'This Could Be Literally Anyone\'s Resume',
    explanation: `Your resume includes ${found.length} generic statements like "${found[0]}" that could apply to any human being on earth. These phrases are resume filler. They tell a recruiter absolutely nothing about what makes YOU different from the 200 other people who applied with the exact same phrases.`,
    fix: `Replace every generic claim with a specific example. Instead of "excellent communication skills," try "Presented quarterly results to C-suite audience of 50+ executives, leading to approval of $1.5M budget increase." Prove it, don't just say it.`,
    examples,
    penalty,
  };
}

function checkClicheObjectives(text: string): RoastProblem | null {
  const lowerText = text.toLowerCase();
  const found = CLICHE_OBJECTIVES.filter((obj) => lowerText.includes(obj));

  if (found.length === 0) return null;

  const examples = findMatchingLines(text, found);
  const severity: Severity = found.length >= 3 ? 'major' : 'minor';
  const penalty = found.length * 5;

  return {
    severity,
    headline: 'Your Objective Statement is a Snooze-Fest',
    explanation: `Your resume contains cliche objective language like "${found[0]}." Objective statements are already considered outdated by most recruiters. A bad one is worse than none at all. "Seeking a challenging position to utilize my skills" tells the recruiter nothing except that you Googled "resume objective examples" in 2005.`,
    fix: `Replace the objective with a professional summary: 2-3 punchy sentences about your top achievements and what you bring to the table. "Software engineer with 5 years of experience building scalable APIs that serve 10M+ daily requests. Reduced infrastructure costs by 40% at [Company]." That is a summary that gets interviews.`,
    examples,
    penalty,
  };
}

function checkSpellingRedFlags(text: string): RoastProblem | null {
  const commonMisspellings: [string, string][] = [
    ['managment', 'management'],
    ['acheive', 'achieve'],
    ['acheived', 'achieved'],
    ['recieve', 'receive'],
    ['recieved', 'received'],
    ['occured', 'occurred'],
    ['occurence', 'occurrence'],
    ['seperate', 'separate'],
    ['definately', 'definitely'],
    ['accomodate', 'accommodate'],
    ['liason', 'liaison'],
    ['millenial', 'millennial'],
    ['calender', 'calendar'],
    ['commited', 'committed'],
    ['developement', 'development'],
    ['enviornment', 'environment'],
    ['excercise', 'exercise'],
    ['independant', 'independent'],
    ['neccessary', 'necessary'],
    ['occassion', 'occasion'],
    ['priviledge', 'privilege'],
    ['profesional', 'professional'],
    ['recomend', 'recommend'],
    ['refered', 'referred'],
    ['succesful', 'successful'],
    ['successfull', 'successful'],
    ['thier', 'their'],
    ['untill', 'until'],
    ['writting', 'writing'],
    ['bussiness', 'business'],
    ['adress', 'address'],
    ['analisis', 'analysis'],
    ['colaborate', 'collaborate'],
    ['comunicate', 'communicate'],
    ['experiance', 'experience'],
    ['impliment', 'implement'],
    ['maintainance', 'maintenance'],
    ['responsibilty', 'responsibility'],
    ['stratagic', 'strategic'],
  ];

  const lowerText = text.toLowerCase();
  const found: { wrong: string; correct: string }[] = [];

  for (const [wrong, correct] of commonMisspellings) {
    if (lowerText.includes(wrong)) {
      found.push({ wrong, correct });
    }
  }

  // Check for double spaces (sloppy formatting)
  const doubleSpaces = (text.match(/[^ \n]  +[^ \n]/g) || []).length;

  // Check for inconsistent capitalization in section headers
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const allCapsLines = lines.filter((l) => l.trim() === l.trim().toUpperCase() && l.trim().length > 3 && l.trim().length < 40);
  const titleCaseLines = lines.filter((l) => /^[A-Z][a-z]/.test(l.trim()) && l.trim().length < 40);
  const inconsistentHeaders = allCapsLines.length > 0 && titleCaseLines.length > 0;

  const totalIssues = found.length + (doubleSpaces > 3 ? 1 : 0) + (inconsistentHeaders ? 1 : 0);

  if (totalIssues === 0) return null;

  const examples: string[] = [];
  for (const f of found.slice(0, 2)) {
    const line = lines.find((l) => l.toLowerCase().includes(f.wrong));
    if (line) examples.push(line.trim());
  }
  if (doubleSpaces > 3) examples.push('(Multiple instances of double spaces detected)');
  if (inconsistentHeaders) examples.push('(Inconsistent header formatting: mix of ALL CAPS and Title Case)');

  const severity: Severity = found.length >= 3 ? 'major' : 'minor';
  const penalty = found.length * 5 + (doubleSpaces > 3 ? 4 : 0) + (inconsistentHeaders ? 3 : 0);

  const issueDescriptions: string[] = [];
  if (found.length > 0) {
    issueDescriptions.push(
      `${found.length} likely spelling error${found.length > 1 ? 's' : ''} ("${found[0].wrong}" should be "${found[0].correct}"${found.length > 1 ? ` and ${found.length - 1} more` : ''})`,
    );
  }
  if (doubleSpaces > 3) issueDescriptions.push(`${doubleSpaces} double spaces`);
  if (inconsistentHeaders) issueDescriptions.push('inconsistent header formatting');

  return {
    severity,
    headline: 'Houston, We Have a Typo Problem',
    explanation: `Found ${issueDescriptions.join(', ')}. A single typo on a resume can disqualify you. It tells recruiters: "This person does not pay attention to detail, and they did not care enough about this application to proofread." If you cannot spell-check a one-page document, why would they trust you with real work?`,
    fix: `Run spell-check (seriously). Then read your resume backward -- word by word -- to catch what spell-check misses. Better yet, have someone else read it. Fresh eyes catch mistakes yours have gone blind to.`,
    examples,
    penalty,
  };
}

/* ------------------------------------------------------------------ */
/*  Identify positives                                                 */
/* ------------------------------------------------------------------ */

function findPositives(text: string): RoastPositive[] {
  const positives: RoastPositive[] = [];
  const lowerText = text.toLowerCase();
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const wordCount = text.split(/\s+/).filter((w) => w.length > 0).length;

  // Check for strong action verbs
  const actionVerbLines = lines.filter((line) => {
    const firstWord = line.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '');
    return STRONG_ACTION_VERBS.includes(firstWord);
  });
  if (actionVerbLines.length >= 3) {
    positives.push({ text: `Strong use of action verbs -- ${actionVerbLines.length} bullet points start with power verbs like "${actionVerbLines[0].trim().split(/\s+/)[0]}." That is how you sound like a doer, not a bystander.` });
  }

  // Check for metrics
  const linesWithNumbers = lines.filter((l) =>
    /\d+[%$kKmMbB]|\$\d|#\d|\d+\s*%|\d+x|\d+\+|\d{1,3},\d{3}/.test(l),
  );
  if (linesWithNumbers.length >= 4) {
    positives.push({ text: `You actually quantified your achievements (${linesWithNumbers.length} lines with metrics). This puts you ahead of 80% of resumes out there.` });
  }

  // Check for good length
  if (wordCount >= 250 && wordCount <= 700) {
    positives.push({ text: `Resume length is in the sweet spot (~${wordCount} words). Concise but substantial.` });
  }

  // Check for all key sections
  const sectionCount = [
    hasSection(text, EXPERIENCE_HEADERS),
    hasSection(text, EDUCATION_HEADERS),
    hasSection(text, SKILLS_HEADERS),
    hasSection(text, SUMMARY_HEADERS),
  ].filter(Boolean).length;
  if (sectionCount >= 3) {
    positives.push({ text: `Good resume structure with ${sectionCount} key sections clearly labeled. ATS systems will parse this correctly.` });
  }

  // Check for bullet points
  const bulletLines = lines.filter((l) =>
    /^\s*[-*\u2022\u25CF\u25CB\u2023\u2043\u25AA\u25AB\u2013\u2014\u27A4>]/.test(l.trim()) ||
    /^\s*\d+[.)]\s/.test(l.trim()),
  );
  if (bulletLines.length >= 5) {
    positives.push({ text: `Good use of bullet points (${bulletLines.length} found). This makes your resume scannable in that critical 6-second window.` });
  }

  // Check for no first-person pronouns
  const pronounMatches = text.match(/\b(I|me|my|myself|mine)\b/g) || [];
  if (pronounMatches.length === 0) {
    positives.push({ text: 'Written in proper implied first person -- no "I" or "my." Professional and clean.' });
  }

  // Check for contact info
  const hasEmail = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(text);
  const hasPhone = /(\+?\d{1,3}[-.\s]?)?\(?\d{2,4}\)?[-.\s]?\d{3,4}[-.\s]?\d{3,4}/.test(text);
  const hasLinkedIn = /linkedin/i.test(text);
  const contactItems = [hasEmail, hasPhone, hasLinkedIn].filter(Boolean).length;
  if (contactItems >= 2) {
    positives.push({ text: `Contact information looks solid (${contactItems}/3 key items found). Recruiters can actually reach you.` });
  }

  // Check for no weak phrases
  const weakFound = WEAK_PHRASES.filter((p) => lowerText.includes(p));
  if (weakFound.length === 0) {
    positives.push({ text: 'No passive language detected. You sound confident and direct -- exactly what recruiters want.' });
  }

  // Check for no buzzwords
  const buzzFound = BUZZWORDS.filter((bw) => {
    const regex = new RegExp(`\\b${bw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i');
    return regex.test(lowerText);
  });
  if (buzzFound.length === 0) {
    positives.push({ text: 'Zero buzzwords -- your resume sounds authentic instead of like a corporate bingo card.' });
  }

  // Dates check
  const dateMatches = text.match(
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december)\s*\.?\s*\d{2,4}|\b20\d{2}\b|\b19\d{2}\b|\bpresent\b|\bcurrent\b/gi,
  ) || [];
  if (dateMatches.length >= 4) {
    positives.push({ text: `Employment dates are clearly listed (${dateMatches.length} date references found). Timeline is transparent.` });
  }

  return positives;
}

/* ------------------------------------------------------------------ */
/*  Main roast function                                                */
/* ------------------------------------------------------------------ */

function roastResume(text: string): RoastResult {
  // Run all checks
  const allProblems: (RoastProblem | null)[] = [
    checkWeakLanguage(text),
    checkMissingMetrics(text),
    checkLength(text),
    checkMissingSections(text),
    checkBuzzwords(text),
    checkFormattingSignals(text),
    checkPronouns(text),
    checkGenericStatements(text),
    checkClicheObjectives(text),
    checkSpellingRedFlags(text),
  ];

  // Filter nulls and sort by penalty descending, then take top 5
  const problems = allProblems
    .filter((p): p is RoastProblem => p !== null)
    .sort((a, b) => b.penalty - a.penalty)
    .slice(0, 5);

  const positives = findPositives(text);

  // Calculate score (start at 100 and deduct)
  const totalPenalty = problems.reduce((sum, p) => sum + p.penalty, 0);
  const score = Math.max(0, Math.min(100, 100 - totalPenalty));

  // Generate breakdown scores
  const lines = text.split('\n').filter((l) => l.trim().length > 0);
  const wordCount = text.split(/\s+/).filter((w) => w.length > 0).length;
  const lowerText = text.toLowerCase();

  // Impact score
  const linesWithNumbers = lines.filter((l) =>
    /\d+[%$kKmMbB]|\$\d|#\d|\d+\s*%|\d+x|\d+\+|\d{1,3},\d{3}/.test(l),
  );
  const metricsRatio = lines.length > 0 ? linesWithNumbers.length / lines.length : 0;
  const actionVerbLines = lines.filter((line) => {
    const firstWord = line.trim().split(/\s+/)[0]?.toLowerCase().replace(/[^a-z]/g, '');
    return STRONG_ACTION_VERBS.includes(firstWord);
  });
  const impactScore = Math.min(100, Math.round(30 + metricsRatio * 100 + (actionVerbLines.length / Math.max(lines.length, 1)) * 80));

  // Language score
  const weakFound = WEAK_PHRASES.filter((p) => lowerText.includes(p)).length;
  const buzzFound = BUZZWORDS.filter((bw) => new RegExp(`\\b${bw.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(lowerText)).length;
  const pronounCount = (text.match(/\b(I|me|my|myself|mine)\b/g) || []).length;
  const languageScore = Math.max(0, Math.min(100, 100 - weakFound * 10 - buzzFound * 6 - pronounCount * 4));

  // Structure score
  const sectionCount = [
    hasSection(text, EXPERIENCE_HEADERS),
    hasSection(text, EDUCATION_HEADERS),
    hasSection(text, SKILLS_HEADERS),
    hasSection(text, SUMMARY_HEADERS),
  ].filter(Boolean).length;
  const bulletLines = lines.filter((l) =>
    /^\s*[-*\u2022\u25CF\u25CB\u2023\u2043\u25AA\u25AB\u2013\u2014\u27A4>]/.test(l.trim()) ||
    /^\s*\d+[.)]\s/.test(l.trim()),
  );
  const bulletRatio = lines.length > 0 ? bulletLines.length / lines.length : 0;
  const lengthScore = (wordCount >= 200 && wordCount <= 800) ? 25 : (wordCount >= 100 && wordCount <= 1000) ? 12 : 0;
  const structureScore = Math.min(100, Math.round(sectionCount * 18 + bulletRatio * 60 + lengthScore));

  // Professionalism score
  const genericFound = GENERIC_STATEMENTS.filter((s) => lowerText.includes(s)).length;
  const clicheFound = CLICHE_OBJECTIVES.filter((o) => lowerText.includes(o)).length;
  const profScore = Math.max(0, Math.min(100, 100 - genericFound * 8 - clicheFound * 10 - pronounCount * 5));

  const breakdown = [
    { label: 'Impact & Metrics', value: impactScore },
    { label: 'Language & Tone', value: languageScore },
    { label: 'Structure & Formatting', value: structureScore },
    { label: 'Professionalism', value: profScore },
  ];

  // Generate verdict
  let verdict: string;
  if (score >= 90) {
    verdict = "Honestly? I'm impressed. This resume is almost recruiter-proof.";
  } else if (score >= 80) {
    verdict = "Not bad at all. A few tweaks and you'll be getting callbacks.";
  } else if (score >= 70) {
    verdict = "Decent foundation, but recruiters have seen better. Let's fix that.";
  } else if (score >= 55) {
    verdict = "We need to talk. This resume is leaving interviews on the table.";
  } else if (score >= 40) {
    verdict = "Your resume needs CPR. But don't worry -- we'll bring it back to life.";
  } else if (score >= 25) {
    verdict = "I've seen cover letters more convincing than this resume.";
  } else {
    verdict = "Is this... is this a resume? We have some serious work to do.";
  }

  return { score, verdict, problems, positives, breakdown };
}

/* ------------------------------------------------------------------ */
/*  Severity badge component                                           */
/* ------------------------------------------------------------------ */

function SeverityBadge({ severity }: { severity: Severity }) {
  const config = {
    critical: { label: 'Critical', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
    major: { label: 'Major', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
    minor: { label: 'Minor', bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' },
  }[severity];

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${config.bg} ${config.text} border ${config.border}`}>
      {config.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function ResumeRoastContent() {
  const [resumeText, setResumeText] = useState('');
  const [result, setResult] = useState<RoastResult | null>(null);
  const [isRoasting, setIsRoasting] = useState(false);

  function handleRoast() {
    if (resumeText.trim().length < 50) return;
    setIsRoasting(true);
    setTimeout(() => {
      setResult(roastResume(resumeText));
      setIsRoasting(false);
    }, 1200);
  }

  function handleReset() {
    setResult(null);
    setResumeText('');
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      <ToolHero
        title="Resume Roast"
        description="Get brutally honest feedback on the top 5 problems with your resume. No sugarcoating -- just the truth about what recruiters really think. 100% private, runs in your browser."
        category="Resume Analysis"
      />

      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Input section */}
        {!result && (
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">
              Paste Your Resume Text
            </h2>
            <p className="text-sm text-gray-500 mb-6">
              Copy the text from your resume and paste it below. We will find the top 5 problems and tell you exactly how to fix them -- with zero sugarcoating.
            </p>

            <textarea
              value={resumeText}
              onChange={(e) => setResumeText(e.target.value)}
              placeholder={`John Smith
john.smith@email.com | (555) 123-4567 | linkedin.com/in/johnsmith | New York, NY

Objective
Seeking a challenging position to utilize my skills...

Experience
Marketing Coordinator | Acme Corp | Jan 2021 - Present
- Responsible for managing social media accounts
- Helped with email marketing campaigns
- Worked on team projects and assisted with reporting
...`}
              className="w-full h-72 p-4 border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-y"
            />

            <div className="flex items-center justify-between mt-4">
              <span className="text-xs text-gray-400">
                {resumeText.trim().length > 0
                  ? `${resumeText.split(/\s+/).filter((w) => w.length > 0).length} words`
                  : 'Minimum 50 characters'}
              </span>
              <button
                onClick={handleRoast}
                disabled={resumeText.trim().length < 50 || isRoasting}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-3 px-8 rounded-xl shadow-lg hover:from-orange-600 hover:to-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isRoasting ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Roasting...
                  </>
                ) : (
                  <><Flame className="w-5 h-5 inline-block mr-1" /> Roast My Resume</>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Results section */}
        {result && (
          <div className="space-y-8">
            {/* Overall roast score */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 text-center">
              <p className="text-sm font-semibold text-orange-600 uppercase tracking-wide mb-4">
                Roast Score
              </p>
              <ScoreGauge score={result.score} label="Resume Health" size={220} />
              <p className="text-xl font-bold text-gray-900 mt-4">
                {result.verdict}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                We found {result.problems.length} problem{result.problems.length !== 1 ? 's' : ''} with your resume, ranked by severity.
              </p>
            </div>

            {/* Score breakdown */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-lg font-bold text-gray-900 mb-6">Score Breakdown</h3>
              <div className="space-y-5">
                {result.breakdown.map((b) => (
                  <ProgressBar key={b.label} value={b.value} label={b.label} />
                ))}
              </div>
            </div>

            {/* Top 5 problems */}
            <div>
              <h3 className="text-2xl font-extrabold text-gray-900 mb-6 flex items-center gap-2">
                <Flame className="w-6 h-6 text-orange-500" /> Top {result.problems.length} Problem{result.problems.length !== 1 ? 's' : ''}
              </h3>

              <div className="space-y-6">
                {result.problems.map((problem, i) => (
                  <div
                    key={i}
                    className={`bg-white rounded-2xl shadow-lg border-l-4 ${
                      problem.severity === 'critical'
                        ? 'border-l-red-500'
                        : problem.severity === 'major'
                          ? 'border-l-orange-500'
                          : 'border-l-amber-400'
                    } p-6 sm:p-8`}
                  >
                    {/* Header row */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-center gap-3">
                        <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-extrabold text-white ${
                          problem.severity === 'critical'
                            ? 'bg-red-500'
                            : problem.severity === 'major'
                              ? 'bg-orange-500'
                              : 'bg-amber-400'
                        }`}>
                          {i + 1}
                        </span>
                        <h4 className="text-lg font-bold text-gray-900 leading-tight">
                          {problem.headline}
                        </h4>
                      </div>
                      <SeverityBadge severity={problem.severity} />
                    </div>

                    {/* Explanation */}
                    <p className="text-gray-700 leading-relaxed mb-4">
                      {problem.explanation}
                    </p>

                    {/* Examples from their resume */}
                    {problem.examples.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-2">
                          Found in your resume:
                        </p>
                        <div className="space-y-2">
                          {problem.examples.map((ex, j) => (
                            <div
                              key={j}
                              className="bg-red-50 border border-red-100 rounded-lg px-4 py-2 text-sm text-red-800 italic"
                            >
                              &ldquo;{ex}&rdquo;
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Fix */}
                    <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                      <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">
                        How to fix it:
                      </p>
                      <p className="text-sm text-green-800 leading-relaxed">
                        {problem.fix}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* The Good Stuff */}
            {result.positives.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-8">
                <h3 className="text-xl font-bold text-green-900 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  The Good Stuff
                </h3>
                <p className="text-sm text-green-700 mb-4">
                  It is not all bad. Here is what you are doing right:
                </p>
                <ul className="space-y-3">
                  {result.positives.map((positive, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      <span className="text-sm text-green-800 leading-relaxed">{positive.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* No positives fallback */}
            {result.positives.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
                <p className="text-lg font-bold text-amber-800 mb-2">
                  Searching for positives...
                </p>
                <p className="text-sm text-amber-700">
                  Look, everyone starts somewhere. The good news is you have a resume -- and now you know exactly what to fix. Use the recommendations above and come back for another roast.
                </p>
              </div>
            )}

            {/* Start over */}
            <div className="text-center">
              <button
                onClick={handleReset}
                className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-xl text-gray-700 font-semibold hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Roast Another Resume
              </button>
            </div>

            {/* CTA */}
            <ToolCTA
              headline="Done getting roasted? Let AI fix your resume."
              description="Superpower Resume takes everything we just roasted and fixes it automatically -- strong action verbs, quantified achievements, zero buzzwords, and ATS-friendly formatting."
              buttonText="Build a Better Resume Now"
            />
          </div>
        )}

        {/* Educational section (shown before roasting) */}
        {!result && (
          <div className="mt-12 space-y-8">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">What Will We Roast?</h3>
              <p className="text-gray-600 mb-6">
                We analyze your resume for the most common mistakes that make recruiters cringe -- and rank the top 5 worst offenders. Here is what we check:
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { name: 'Weak/Passive Language', desc: '"Responsible for" and "helped with" make you sound like a bystander, not a leader.' },
                  { name: 'Missing Metrics', desc: 'No numbers = no proof. Recruiters want to see the impact, not just the activity.' },
                  { name: 'Resume Length', desc: 'Too short looks inexperienced. Too long says you cannot edit. Both are bad.' },
                  { name: 'Missing Sections', desc: 'No Skills section? No Summary? ATS systems will reject you before a human even looks.' },
                  { name: 'Buzzword Overload', desc: '"Synergy," "team player," "self-starter" -- these words have lost all meaning.' },
                  { name: 'Formatting Issues', desc: 'Walls of text, no bullet points, inconsistent structure. Visual chaos = instant skip.' },
                  { name: 'First-Person Pronouns', desc: '"I did this, I did that" -- resumes use implied first person. Drop the "I."' },
                  { name: 'Generic Statements', desc: '"Excellent communication skills" could be on literally anyone\'s resume.' },
                  { name: 'Cliche Objectives', desc: '"Seeking a challenging position" is the resume equivalent of a yawn.' },
                  { name: 'Spelling & Grammar', desc: 'One typo can cost you the interview. We catch the common ones.' },
                ].map((item) => (
                  <div key={item.name} className="p-4 bg-orange-50 rounded-xl border border-orange-100">
                    <h4 className="font-bold text-gray-900 text-sm">{item.name}</h4>
                    <p className="text-sm text-gray-600 mt-0.5">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Why Brutal Honesty?</h3>
              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Most resume feedback is too polite to be useful. Your friend says "looks great!" Your career counselor gives vague advice. Meanwhile, the recruiter who rejected you in 6 seconds never tells you why.
                </p>
                <p>
                  This tool gives you the feedback that hiring managers are thinking but never saying out loud. It is the difference between "your resume could use some improvement" and "your objective statement sounds like it was written in 2003 and your bullet points have zero measurable results."
                </p>
                <p>
                  The goal is not to be mean -- it is to be <strong>helpful</strong>. Every roast comes with a specific, actionable fix. Think of it as tough love for your career.
                </p>
                <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
                  <strong>Sources &amp; Further Reading:</strong>{' '}
                  <a href="https://www.indeed.com/career-advice/resumes-cover-letters/resume-mistakes" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: Common Resume Mistakes to Avoid</a>
                  {' · '}
                  <a href="https://hbr.org/2014/12/how-to-make-your-resume-stand-out" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: How to Make Your Resume Stand Out</a>
                  {' · '}
                  <a href="https://www.careeronestop.org/JobSearch/Resumes/resume-guide.aspx" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">CareerOneStop Resume Guide (U.S. DOL)</a>
                </p>
              </div>
            </div>

            <ToolCTA
              headline="Skip the roast -- build a resume that's already perfect"
              description="Superpower Resume uses AI to create resumes with strong action verbs, quantified achievements, and ATS-optimized formatting from the start. No roasting required."
              buttonText="Build My Resume"
            />
          </div>
        )}
      </div>
    </div>
  );
}
